import CommentIcon from '@atlaskit/icon/core/comment';
import CommentAddIcon from '@atlaskit/icon/core/comment-add';
import GrowDiagonalIcon from '@atlaskit/icon/core/grow-diagonal';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { messages } from '@databases/intl/messages';
import React, { type FunctionComponent, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

const STANDARD_ICON_SIZE = '24px';

const FloatingOverlayStyles = xcss({
  position: 'absolute',
  top: 'space.050',
  right: 'space.050',
  opacity: 0,
  transition: 'opacity 0.2s ease-in-out',
  pointerEvents: 'none',
  boxShadow: 'elevation.shadow.overlay',
  backgroundColor: 'elevation.surface.overlay',
  borderRadius: 'radius.small',
  display: 'flex',
  alignItems: 'center',
});

const FloatingOverlayVisibleStyles = xcss({
  opacity: 1,
  pointerEvents: 'auto',
});

const ButtonsContainerStyles = xcss({
  display: 'flex',
  alignItems: 'center',
});

const BaseButtonStyles = xcss({
  width: STANDARD_ICON_SIZE,
  height: STANDARD_ICON_SIZE,
  padding: 'space.0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'box-shadow 0.2s, background-color 0.2s',
  ':hover': {
    cursor: 'pointer',
    backgroundColor: 'color.background.neutral.hovered',
    boxShadow: 'elevation.shadow.overlay',
  },
});

const CommentButtonStyles = xcss({
  minWidth: STANDARD_ICON_SIZE,
  width: 'auto',
  paddingInline: 'space.075',
});

const AddThreadButtonStyles = xcss({
  width: STANDARD_ICON_SIZE,
  height: STANDARD_ICON_SIZE,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'box-shadow 0.2s, background-color 0.2s',
  ':hover': {
    cursor: 'pointer',
    backgroundColor: 'color.background.neutral.hovered',
    boxShadow: 'elevation.shadow.overlay',
  },
});

const ViewThreadButtonSelectedStyles = xcss({
  ':hover': {
    backgroundColor: 'color.background.accent.blue.subtlest',
  },
});

const DividerStyles = xcss({
  width: '0.75px',
  height: STANDARD_ICON_SIZE,
  backgroundColor: 'color.background.accent.gray.subtlest.hovered',
});

export interface FloatingCellActionButtonsProps {
  onCommentClick: (e: React.MouseEvent) => void;
  commentToggled: boolean;
  hasThreads: boolean;
  commentCount: number;
  shouldShowHoverButton: boolean;
  valueId: string;
  isHovered: boolean;
  isFirstField: boolean;
  handleExpandEntry: (e: React.MouseEvent) => void;
  panel: boolean;
}

const FloatingCellActionButtons: FunctionComponent<FloatingCellActionButtonsProps> = ({
  onCommentClick,
  commentToggled,
  hasThreads,
  commentCount,
  shouldShowHoverButton,
  valueId,
  isHovered,
  isFirstField,
  handleExpandEntry,
  panel,
}) => {
  const { formatMessage } = useIntl();

  const commentString = useMemo(() => {
    return commentCount > 9 ? '9+' : commentCount.toString();
  }, [commentCount]);

  const containerStyles = useMemo(() => {
    return isHovered
      ? [FloatingOverlayStyles, FloatingOverlayVisibleStyles]
      : [FloatingOverlayStyles];
  }, [isHovered]);

  const renderCommentButton = () => {
    if (hasThreads) {
      return (
        <Tooltip
          position="top"
          content={formatMessage(
            messages['comments.show-comment-thread-button-tooltip'],
            {
              isMulti: commentCount,
            }
          )}
        >
          <Box
            onClick={onCommentClick}
            xcss={[
              BaseButtonStyles,
              CommentButtonStyles,
              commentToggled && ViewThreadButtonSelectedStyles,
            ]}
            id={`cell-view-thread-button-${valueId}`}
            testId="cell-view-thread-button"
          >
            <Inline
              space="space.050"
              alignBlock="center"
              xcss={xcss({
                color: commentToggled ? 'color.text.selected' : 'color.text',
              })}
            >
              <CommentIcon
                label={formatMessage(
                  messages['comments.view-comment-thread-button-label']
                )}
                size="small"
                color={
                  commentToggled ? token('color.icon.accent.blue') : token('color.icon')
                }
              />
              <Box xcss={xcss({ font: 'font.body.small', whiteSpace: 'nowrap' })}>
                {commentString}
              </Box>
            </Inline>
          </Box>
        </Tooltip>
      );
    }

    if (shouldShowHoverButton) {
      return (
        <Tooltip
          position="top"
          content={formatMessage(messages['comments.add-comment-thread-button-label'])}
        >
          <Box
            xcss={AddThreadButtonStyles}
            onClick={onCommentClick}
            id={`cell-add-thread-button-${valueId}`}
            testId="cell-add-thread-button"
          >
            <CommentAddIcon
              label={formatMessage(messages['comments.add-comment-thread-button-label'])}
              size={'small'}
              color={token('color.icon')}
            />
          </Box>
        </Tooltip>
      );
    }

    return null;
  };

  const renderExpandButton = () => (
    <Tooltip position="top" content={formatMessage(messages['entry-actions.expand'])}>
      <Box
        xcss={BaseButtonStyles}
        onClick={handleExpandEntry}
        id={`cell-expand-entry-button-${valueId}`}
      >
        <GrowDiagonalIcon
          label={formatMessage(messages['entry-actions.expand'])}
          size={'small'}
          color={token('color.icon')}
        />
      </Box>
    </Tooltip>
  );

  return (
    <Box xcss={containerStyles}>
      <Box xcss={ButtonsContainerStyles}>
        {isFirstField && !panel && (
          <>
            {renderExpandButton()}
            <Box xcss={DividerStyles} />
          </>
        )}
        {renderCommentButton()}
      </Box>
    </Box>
  );
};

export default FloatingCellActionButtons;
